import { Integrations as TracingIntegrations } from '@sentry/tracing';
import * as Sentry from '@sentry/nextjs';
import {
    CaptureConsole as CaptureConsoleIntegration,
    ExtraErrorData as ExtraErrorDataIntegration,
} from '@sentry/integrations';
import { getCurrentEnvironment } from '@pb/services';
import { tracesSampler } from './sentry.utils';
import { TRACES_SAMPLE_RATE } from './sentry.constants';
import { TSentryClientConfig } from '.';

// This file configures the initialization of Sentry on the browser.
// The config you add here will be used whenever a page is visited.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

// Set Data Source Name (DSN) via default env vars or manually supply `dsn` below
const SENTRY_DSN = process.env.SENTRY_DSN || process.env.NEXT_PUBLIC_SENTRY_DSN;

export const initSentryClient = ({
    captureConsoleLevels = ['error', 'warn'],
    debug = false,
    dsn,
    extraErrorDataDepth,
    tracesSampleRate = TRACES_SAMPLE_RATE,
}: TSentryClientConfig) => {
    const integrations = [
        new TracingIntegrations.BrowserTracing(),
        new CaptureConsoleIntegration({ levels: captureConsoleLevels }),
        ...(extraErrorDataDepth
            ? [
                  new ExtraErrorDataIntegration({
                      // limit of how deep the object serializer should go. Anything deeper than limit will
                      // be replaced with standard Node.js REPL notation of [Object], [Array], [Function] or
                      // a primitive value. Defaults to 3.
                      depth: extraErrorDataDepth,
                  }),
              ]
            : []),
    ];

    Sentry.init({
        debug,
        dsn: SENTRY_DSN || dsn,
        environment: getCurrentEnvironment(),
        integrations: [...integrations],
        tracesSampler: /* istanbul ignore next */ samplingContext =>
            tracesSampler(samplingContext, tracesSampleRate),
        // ...
        // Note: if you want to override the automatic release value, do not set a
        // `release` value here - use the environment variable `SENTRY_RELEASE`, so
        // that it will also get attached to your source maps
    });
};
